import React, { useState, useEffect } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import Papa from "papaparse";
import { db } from "../firebase.js";
import * as utils from "./Utils.js";
import { shuttleStops } from "../data/transportationLocations.js";

// Map pickup instruction keywords → shuttle name
const SHUTTLE_KEYWORD_MAP = [
  { keywords: ["marshall slocum", "29 kay", "kay st"], shuttle: "Shuttle A" },
  { keywords: ["wellington", "thames"], shuttle: "Shuttle B" },
  { keywords: ["perrotti"], shuttle: "Shuttle C" },
  { keywords: ["marriott"], shuttle: "Shuttle D" },
  { keywords: ["sonesta", "pell"], shuttle: "Shuttle E" },
  { keywords: ["residence inn", "325 west main", "rodeway", "31 west main", "wayfinder", "admiral kalbfus"], shuttle: "Shuttle F" },
  { keywords: ["49 maple", "maple ave"], shuttle: "Shuttle G" },
];

function inferShuttle(pickupInstruction) {
  if (!pickupInstruction) return null;
  const lower = pickupInstruction.toLowerCase();
  for (const { keywords, shuttle } of SHUTTLE_KEYWORD_MAP) {
    if (keywords.some((k) => lower.includes(k))) return shuttle;
  }
  return null; // walking or unknown
}

const AdminReservations = () => {

  const [currentUser, setCurrentUser] = useState(null);

  const [reservations, setReservations] = useState([]);
  const [guestsById, setGuestsById] = useState({});
  const [expandedResId, setExpandedResId] = useState(null);
  const [expandedGuestId, setExpandedGuestId] = useState(null);
  const [events, setEvents] = useState([]);

  const [creatingReservation, setCreatingReservation] = useState(false);
  const [newReservation, setNewReservation] = useState(null);
  const [newGuests, setNewGuests] = useState([]);
  const [nextGuestId, setNextGuestId] = useState(1);

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
        fetchReservations();
        fetchGuests();
        fetchEvents();
      } else {
        window.location.href = "/login";
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchReservations = async () => {
    const snapshot = await getDocs(collection(db, "reservations"));

    const loaded = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    setReservations(loaded);
  };

  const fetchGuests = async () => {
    const snapshot = await getDocs(collection(db, "guests"));

    const loaded = {};
    let highestId = 0;

    snapshot.docs.forEach((docSnap) => {
      loaded[docSnap.id] = docSnap.data();

      const numericId = parseInt(docSnap.id);

      if (!isNaN(numericId) && numericId > highestId) {
        highestId = numericId;
      }
    });

    setGuestsById(loaded);
    setNextGuestId(highestId + 1);
  };

  const fetchEvents = async () => {
    const snapshot = await getDocs(collection(db, "events"));

    const loaded = snapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => {
        const aStart = a.start?.toDate ? a.start.toDate() : new Date(a.start);
        const bStart = b.start?.toDate ? b.start.toDate() : new Date(b.start);
        return aStart - bStart;
      });

    setEvents(loaded);
  };

  const createNewReservation = () => {

    const newRes = {
      name: "",
      lookup_names: "",
      address: {
        street: "",
        apartment: "",
        city: "",
        state: "",
        zip: "",
        country: "USA",
      },
      guests: [],
      createdAt: "",
      updatedAt: "",
    };

    const firstGuest = {
      id: nextGuestId,
      name: "",
      over21: false,
      type: "adult",
      dietary_restrictions: "",
      invitedEvents: []
    };

    setCreatingReservation(true);
    setNewReservation(newRes);
    setNewGuests([firstGuest]);
  };

  const addGuest = () => {

    const newId = nextGuestId + newGuests.length;

    const guest = {
      id: newId,
      name: "",
      over21: false,
      type: "adult",
      dietary_restrictions: "",
      invitedEvents: []
    };

    setNewGuests([...newGuests, guest]);
  };

  const removeGuest = (index) => {
    const copy = [...newGuests];
    copy.splice(index, 1);
    setNewGuests(copy);
  };

  const handleGuestChange = (index, field, value) => {
    const copy = [...newGuests];
    copy[index] = { ...copy[index], [field]: value };
    setNewGuests(copy);
  };

  const toggleGuestEvent = (guestIndex, eventId) => {

    const copy = [...newGuests];
    const guest = copy[guestIndex];

    if (guest.invitedEvents.includes(eventId)) {
      guest.invitedEvents = guest.invitedEvents.filter((id) => id !== eventId);
    } else {
      guest.invitedEvents = [...guest.invitedEvents, eventId];
    }

    copy[guestIndex] = guest;
    setNewGuests(copy);
  };

  const [csvPreview, setCsvPreview] = useState(null); // array of { name, pickup, inferredShuttle, matchedGuestId, matchedGuestName, matchedResName }
  const [savingShuttles, setSavingShuttles] = useState(false);

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        // Detect columns — try common names
        const rows = results.data;
        if (!rows.length) return;
        const keys = Object.keys(rows[0]);
        const nameKey = keys.find((k) => /name/i.test(k)) || keys[0];
        const pickupKey = keys.find((k) => /pick.?up|instruction|shuttle|location/i.test(k)) || keys[1];

        // Build a normalized full-name → { guestId, guest } lookup
        const guestByName = {};
        Object.entries(guestsById).forEach(([guestId, guest]) => {
          if (guest.name) {
            guestByName[guest.name.trim().toLowerCase()] = { guestId, guest };
          }
        });

        const preview = rows.map((row) => {
          const rawName = (row[nameKey] || "").trim();
          const pickup = (row[pickupKey] || "").trim();
          const inferredShuttle = inferShuttle(pickup);

          // Match to a guest by full name (case-insensitive)
          const matched = guestByName[rawName.toLowerCase()];
          let matchedResName = null;
          if (matched) {
            const res = reservations.find((r) => r.id === matched.guest.reservation_id);
            matchedResName = res?.name || null;
          }

          return {
            name: rawName,
            pickup,
            inferredShuttle,
            matchedGuestId: matched?.guestId || null,
            matchedGuestName: matched?.guest.name || null,
            matchedResName,
          };
        });

        setCsvPreview(preview);
        e.target.value = "";
      },
    });
  };

  const saveShuttleAssignments = async () => {
    if (!csvPreview) return;
    setSavingShuttles(true);
    const toSave = csvPreview.filter((r) => r.matchedGuestId);
    for (const row of toSave) {
      await updateDoc(doc(db, "guests", row.matchedGuestId), {
        shuttle: row.inferredShuttle || null,
      });
    }
    setGuestsById((prev) => {
      const updated = { ...prev };
      toSave.forEach((row) => {
        if (updated[row.matchedGuestId]) {
          updated[row.matchedGuestId] = { ...updated[row.matchedGuestId], shuttle: row.inferredShuttle || null };
        }
      });
      return updated;
    });
    setSavingShuttles(false);
    setCsvPreview(null);
    alert(`Saved shuttle assignments for ${toSave.length} guests.`);
  };

  const saveReservation = async () => {

    if (!newReservation) return;

    const reservationRef = doc(collection(db, "reservations"));
    const reservationId = reservationRef.id;

    const guestIds = newGuests.map((g) => g.id.toString());

    const reservationData = {
      ...newReservation,
      id: reservationId,
      lookup_names: newReservation.lookup_names
        .split(",")
        .map((n) => n.trim()),
      guests: guestIds,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(reservationRef, reservationData);

    for (const guest of newGuests) {

      const guestEvents = guest.invitedEvents.map((eventId) => ({
        id: eventId,
        meal_id: "",
        rsvp: ""
      }));

      await setDoc(
        doc(db, "guests", guest.id.toString()),
        {
          id: guest.id.toString(),
          name: guest.name,
          reservation_id: reservationId,
          type: guest.type,
          over21: guest.over21,
          dietary_restrictions: "",
          events: guestEvents,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }

    alert("Reservation created!");

    setCreatingReservation(false);
    setNewReservation(null);
    setNewGuests([]);

    fetchReservations();
    fetchGuests();
  };

  return (
    <div className="AdminReservations">

      <div className="centered-box wedding-page-block">

        <div className="wedding-centerpiece">
          <p style={{ margin: "15px 0 0 0", width: "100%" }}>
            <a
              style={{
                textDecoration: "none",
                color: "#00233f",
                padding: "5px",
                border: "1px solid transparent",
              }}
              href="/admin"
            >
              <b>← Back to Admin</b>
            </a>
          </p>

          <p className="wedding-page-title">Reservations</p>
        </div>

        <div className="text-end mb-3">
          <a className="btn btn-navyblue" onClick={createNewReservation}>
            Create Reservation
          </a>
        </div>

        {creatingReservation && newReservation && (

          <div className="card p-4 shadow-sm mb-4">

            <h5>Create Reservation</h5>

            <div className="mb-3">
              <label className="form-label">Invite Name</label>
              <input
                className="form-control"
                value={newReservation.name}
                onChange={(e) =>
                  setNewReservation({
                    ...newReservation,
                    name: e.target.value,
                  })
                }
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Lookup Names (CSV)</label>
              <input
                className="form-control"
                value={newReservation.lookup_names}
                onChange={(e) =>
                  setNewReservation({
                    ...newReservation,
                    lookup_names: e.target.value,
                  })
                }
              />
            </div>

            <h6>Address</h6>

            <input
              className="form-control mb-2"
              placeholder="Street"
              value={newReservation.address.street}
              onChange={(e) =>
                setNewReservation({
                  ...newReservation,
                  address: {
                    ...newReservation.address,
                    street: e.target.value,
                  },
                })
              }
            />

            <input
              className="form-control mb-2"
              placeholder="Apartment"
              value={newReservation.address.apartment}
              onChange={(e) =>
                setNewReservation({
                  ...newReservation,
                  address: {
                    ...newReservation.address,
                    apartment: e.target.value,
                  },
                })
              }
            />

            <input
              className="form-control mb-2"
              placeholder="City"
              value={newReservation.address.city}
              onChange={(e) =>
                setNewReservation({
                  ...newReservation,
                  address: {
                    ...newReservation.address,
                    city: e.target.value,
                  },
                })
              }
            />

            <input
              className="form-control mb-2"
              placeholder="State"
              value={newReservation.address.state}
              onChange={(e) =>
                setNewReservation({
                  ...newReservation,
                  address: {
                    ...newReservation.address,
                    state: e.target.value,
                  },
                })
              }
            />

            <input
              className="form-control mb-2"
              placeholder="ZIP"
              value={newReservation.address.zip}
              onChange={(e) =>
                setNewReservation({
                  ...newReservation,
                  address: {
                    ...newReservation.address,
                    zip: e.target.value,
                  },
                })
              }
            />

            <input
              className="form-control mb-4"
              placeholder="Country"
              value={newReservation.address.country}
              onChange={(e) =>
                setNewReservation({
                  ...newReservation,
                  address: {
                    ...newReservation.address,
                    country: e.target.value,
                  },
                })
              }
            />

            <h6>Guests</h6>

            {newGuests.map((guest, index) => (
              <div key={guest.id} className="border p-3 mb-3">

                <p><b>Guest #{guest.id}</b></p>

                <input
                  className="form-control mb-2"
                  placeholder="Full Name"
                  value={guest.name}
                  onChange={(e) =>
                    handleGuestChange(index, "name", e.target.value)
                  }
                />

                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={guest.over21}
                    onChange={(e) =>
                      handleGuestChange(index, "over21", e.target.checked)
                    }
                  />
                  <label className="form-check-label">Over 21</label>
                </div>

                <div className="mb-2">

                  <label>Type</label>

                  <div>

                    <label>
                      <input
                        type="radio"
                        checked={guest.type === "adult"}
                        onChange={() =>
                          handleGuestChange(index, "type", "adult")
                        }
                      /> Adult
                    </label>

                    <label className="ms-3">
                      <input
                        type="radio"
                        checked={guest.type === "child"}
                        onChange={() =>
                          handleGuestChange(index, "type", "child")
                        }
                      /> Child
                    </label>

                    <label className="ms-3">
                      <input
                        type="radio"
                        checked={guest.type === "guest"}
                        onChange={() =>
                          handleGuestChange(index, "type", "guest")
                        }
                      /> Guest
                    </label>

                  </div>

                </div>

                <div className="mt-3">

                  <label><b>Invited Events</b></label>

                  {events.map((evt) => (

                    <div key={evt.id} className="form-check">

                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={guest.invitedEvents.includes(evt.id)}
                        onChange={() => toggleGuestEvent(index, evt.id)}
                      />

                      <label className="form-check-label">
                        {evt.name}
                      </label>

                    </div>

                  ))}

                </div>

                <button
                  className="btn btn-sm btn-outline-danger mt-3"
                  onClick={() => removeGuest(index)}
                >
                  Remove Guest
                </button>

              </div>
            ))}

            <button
              className="btn btn-outline-navyblue"
              onClick={addGuest}
            >
              Add Guest
            </button>

            <div className="text-end mt-4">
              <button
                className="btn btn-navyblue"
                onClick={saveReservation}
              >
                Create Reservation
              </button>
            </div>

          </div>
        )}

        {/* Shuttle CSV Upload */}
        <div className="card p-3 mb-4">
          <h6 className="mb-2">Assign Shuttles via Spreadsheet</h6>
          <p className="text-muted" style={{ fontSize: 13 }}>
            Export your spreadsheet as CSV. The first column should be the guest name, the second column the pickup instruction. The shuttle will be inferred from the pickup text.
          </p>
          <input type="file" accept=".csv" className="form-control form-control-sm mb-3" onChange={handleCsvUpload} />

          {csvPreview && (
            <>
              <table className="table table-sm table-bordered" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Spreadsheet Name</th>
                    <th>Pickup Instruction</th>
                    <th>Inferred Shuttle</th>
                    <th>Matched Guest</th>
                    <th>Reservation</th>
                  </tr>
                </thead>
                <tbody>
                  {csvPreview.map((row, i) => (
                    <tr key={i} style={{ backgroundColor: row.matchedGuestId ? undefined : '#fff3cd' }}>
                      <td>{row.name}</td>
                      <td>{row.pickup}</td>
                      <td>{row.inferredShuttle || <em className="text-muted">walking / unknown</em>}</td>
                      <td>{row.matchedGuestName || <em className="text-danger">no match</em>}</td>
                      <td>{row.matchedResName || <em className="text-muted">—</em>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="d-flex gap-2">
                <button className="btn btn-navyblue btn-sm" onClick={saveShuttleAssignments} disabled={savingShuttles}>
                  {savingShuttles ? "Saving..." : `Save ${csvPreview.filter((r) => r.matchedGuestId).length} assignments`}
                </button>
                <button className="btn btn-outline-navyblue btn-sm" onClick={() => setCsvPreview(null)}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>

        <table className="table">

          <thead>
            <tr>
              <th>Invite Name / ID</th>
              <th>Address</th>
              <th>Guest Count</th>
              <th>Reception RSVP</th>
            </tr>
          </thead>

          <tbody>

            {reservations.map((res) => {

              const receptionGuests = (res.guests || []).map(
                (id) => guestsById[id]
              );

              const receptionRSVP = receptionGuests.some(
                (g) => g?.events?.find((e) => e.id === "reception")?.rsvp === "yes"
              )
                ? "yes"
                : receptionGuests.some(
                    (g) =>
                      g?.events?.find((e) => e.id === "reception")?.rsvp === "no"
                  )
                ? "no"
                : "pending";

              const expanded = expandedResId === res.id;

              return (
                <React.Fragment key={res.id}>

                  <tr
                    onClick={() =>
                      setExpandedResId(expanded ? null : res.id)
                    }
                    style={{ cursor: "pointer" }}
                  >

                    <td>
                      {res.name}
                      <br />
                      <small>
                        ID: <code>{res.id}</code>
                      </small>
                    </td>

                    <td>
                      {res.address?.street}, {res.address?.apartment}
                      <br />
                      {res.address?.city}, {res.address?.state} {res.address?.zip}
                    </td>

                    <td>{(res.guests || []).length}</td>

                    <td>{receptionRSVP}</td>

                  </tr>

                </React.Fragment>
              );
            })}

          </tbody>

        </table>

      </div>

      <div className="centered-box">
        {utils.displayCheckerboard(20, 42, ["black", "ivory"])}
      </div>

    </div>
  );
};

export default AdminReservations;
